import { useState, type FormEvent } from 'react';
import { api, ApiRequestError } from '../../lib/api';
import { addDays } from '@shared/time';
import { formatLocalDate } from '../lib/format';
import { useI18n } from '../lib/i18n';
import type { CustomerDetails } from '../types';

interface Props {
  slug: string;
  serviceId: string;
  serviceName: string;
  providerId: string | null;
  locationId: string | null;
  providerName: string | null;
  /** The day the customer was looking at when they found nothing. */
  date: string;
  /** How far ahead this business takes bookings. */
  maxAdvanceDays: number;
  /** Whatever the customer has already typed, so they don't type it twice. */
  details: CustomerDetails;
  onClose: () => void;
}

interface WaitlistResponse {
  waitlist_entry: { id: string; earliest_date: string; latest_date: string };
}

/**
 * "Tell me when something opens." Offered when a day has no times. It books
 * nothing and holds nothing — the note the customer gets says so — so a
 * waitlist ask can never collide with a real booking.
 */
export function WaitlistForm(props: Props) {
  const { t, locale } = useI18n();
  const { slug, serviceId, serviceName, providerId, locationId, providerName, date, maxAdvanceDays, details, onClose } = props;
  // A week from the day they were looking at is the ask most people mean;
  // never past the last day this business takes bookings for.
  const horizon = addDays(new Date().toISOString().slice(0, 10), maxAdvanceDays);
  const suggested = addDays(date, 7);
  const [earliest, setEarliest] = useState(date);
  const [latest, setLatest] = useState(suggested > horizon ? horizon : suggested);
  const [name, setName] = useState(details.name);
  const [email, setEmail] = useState(details.email);
  const [phone, setPhone] = useState(details.phone ?? '');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ earliest: string; latest: string } | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim() || !email.trim()) {
      setError(t('waitlist.need_name_email'));
      return;
    }
    if (latest < earliest) {
      setError(t('waitlist.end_after_start'));
      return;
    }
    setBusy(true);
    try {
      const res = await api<WaitlistResponse>(`/public/${slug}/waitlist`, {
        method: 'POST',
        json: {
          service_id: serviceId,
          provider_id: providerId,
          location_id: locationId ?? undefined,
          earliest_date: earliest,
          latest_date: latest,
          note: note.trim() === '' ? null : note.trim(),
          customer: {
            name: name.trim(),
            email: email.trim(),
            phone: phone.trim() === '' ? null : phone.trim(),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        },
      });
      setDone({ earliest: res.waitlist_entry.earliest_date, latest: res.waitlist_entry.latest_date });
    } catch (err) {
      setError(err instanceof ApiRequestError
        ? err.message
        : t('waitlist.could_not_add'));
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="waitlist waitlist-done" role="status">
        <h4>{t('waitlist.on_the_list')}</h4>
        <p>
          {t('waitlist.we_will_email', {
            email,
            service: serviceName,
            from: formatLocalDate(done.earliest, {}, locale),
            until: formatLocalDate(done.latest, {}, locale),
          })}
        </p>
        <p className="hint">{t('waitlist.not_held')}</p>
        <button type="button" className="btn" onClick={onClose}>{t('waitlist.back_to_times')}</button>
      </div>
    );
  }

  return (
    <form className="waitlist" onSubmit={submit} noValidate>
      <h4>{t('waitlist.tell_me')}</h4>
      <p className="hint">
        We'll email you if a {serviceName}{providerName ? ` with ${providerName}` : ''} appointment comes free.
        This doesn't book or hold anything.
      </p>
      {error && <p className="error" role="alert">{error}</p>}
      <div className="waitlist-dates">
        <div className="field">
          <label htmlFor="wl-from">{t('waitlist.from')}</label>
          <input id="wl-from" type="date" value={earliest} min={new Date().toISOString().slice(0, 10)} max={horizon}
                 onChange={(e) => setEarliest(e.target.value)} required />
        </div>
        <div className="field">
          <label htmlFor="wl-until">{t('waitlist.until')}</label>
          <input id="wl-until" type="date" value={latest} min={earliest} max={horizon}
                 onChange={(e) => setLatest(e.target.value)} required />
        </div>
      </div>
      <div className="field">
        <label htmlFor="wl-name">{t('booking.full_name')}</label>
        <input id="wl-name" type="text" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" required />
      </div>
      <div className="field">
        <label htmlFor="wl-email">{t('booking.email')}</label>
        <input id="wl-email" type="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required />
      </div>
      <div className="field">
        <label htmlFor="wl-phone">{t('booking.phone_label')} <span className="muted">{t('booking.optional_marker')}</span></label>
        <input id="wl-phone" type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" />
      </div>
      <div className="field">
        <label htmlFor="wl-note">{t('booking.notes')} <span className="muted">{t('booking.optional_marker')}</span></label>
        <textarea id="wl-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={500} />
      </div>
      <div className="actions">
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? t('waitlist.adding') : t('waitlist.add_me')}
        </button>
        <button type="button" className="btn" onClick={onClose} disabled={busy}>{t('waitlist.cancel')}</button>
      </div>
    </form>
  );
}
